// Automatic-ability engine: trigger scan → telegraph → effect (plan §18).

import { abilityDef, defs, unitDef } from '../../content/registry';
import { TUNING } from '../../content/tuning';
import type { AbilityDef, EffectRef } from '../../content/types';
import { dist, vec } from '../../shared/vec';
import type { Sim } from '../sim';
import { physicalCorridorClear } from '../mobility';
import { squadBodyRadius } from './gates';
import type { EntId, Squad } from '../state';
import { armorReductionOf } from '../state';
import { consumeMarksNear } from './corpse';

const dt = TUNING.dt;

function hasChargeEffect(def: AbilityDef): boolean {
  return def.effects.some((effect) => effect.fx === 'charge');
}

function chargeAllowedByCommand(s: Squad): boolean {
  return s.command.kind !== 'move' && s.command.kind !== 'withdraw';
}

/** Evaluate a trigger; returns triggering entity id (or self id) when fired, null otherwise. */
function evalTrigger(sim: Sim, s: Squad, def: AbilityDef): EntId | null {
  const t = def.trigger;
  switch (t.when) {
    case 'targetedByRangedSquads': {
      let count = 0;
      for (const e of sim.state.squads.values()) {
        if (!sim.areHostile(e.owner, s.owner) || e.targetId !== s.id) continue;
        const ed = unitDef(e.defId);
        if ((ed.attack?.range ?? 0) > 4 && dist(e.pos, s.pos) <= (ed.attack?.range ?? 0) * 1.3) count++;
      }
      return count >= t.count ? s.id : null;
    }
    case 'enemyBelowHealth': {
      if (s.targetId === null) return null;
      const target = sim.state.squads.get(s.targetId);
      if (!target) return null;
      return target.health / target.maxHealth < t.fraction ? target.id : null;
    }
    case 'chargedBy': {
      for (const e of sim.state.squads.values()) {
        if (!sim.areHostile(e.owner, s.owner)) continue;
        const ed = unitDef(e.defId);
        if (!t.sizeTags.includes(ed.sizeTag)) continue;
        if (dist(e.pos, s.pos) <= 6 && e.moveTarget && dist(e.moveTarget, s.pos) < dist(e.pos, s.pos) + 2) {
          return e.id;
        }
      }
      return null;
    }
    case 'largeEnemyInRange': {
      for (const e of sim.state.squads.values()) {
        if (!sim.areHostile(e.owner, s.owner)) continue;
        const ed = unitDef(e.defId);
        if ((ed.sizeTag === 'large' || ed.sizeTag === 'giant') && dist(e.pos, s.pos) <= t.range) return e.id;
      }
      return null;
    }
    case 'attackingTargetTag': {
      if (s.targetId === null) return null;
      if (t.tag === 'boss') {
        const target = sim.state.squads.get(s.targetId);
        return target?.isBoss ? target.id : null;
      }
      const b = sim.state.buildings.get(s.targetId);
      return b ? b.id : null;
    }
    case 'selfBelowHealth': {
      if (t.oncePerLife && s.abilityFiredOnce[def.id]) return null;
      return s.health / s.maxHealth < t.fraction ? s.id : null;
    }
    case 'timer':
      // cadence is enforced by the internal cooldown; fires whenever off cooldown
      return s.id;
    case 'surroundedBy': {
      let count = 0;
      for (const e of sim.state.squads.values()) {
        if (!sim.areHostile(e.owner, s.owner)) continue;
        if (t.sizeTags && !t.sizeTags.includes(unitDef(e.defId).sizeTag)) continue;
        if (dist(e.pos, s.pos) <= t.range) count++;
      }
      return count >= t.count ? s.id : null;
    }
    case 'targetIsolated': {
      if (s.targetId === null) return null;
      const target = sim.state.squads.get(s.targetId);
      if (!target) return null;
      for (const e of sim.state.squads.values()) {
        if (!sim.areAllies(e.owner, target.owner) || e.id === target.id) continue;
        if (dist(e.pos, target.pos) <= t.range) return null;
      }
      return target.id;
    }
    case 'nthAttackVsStructure':
      return s.structureAttackCount >= t.n ? (s.targetId ?? s.id) : null;
    case 'allyDiedNearby': {
      for (const d of sim.state.recentDeaths) {
        if (!sim.areAllies(d.owner, s.owner)) continue;
        if (sim.state.time - d.at > 1) continue;
        if (dist(d.pos, s.pos) <= t.range) return s.id;
      }
      return null;
    }
    case 'escortSquadDied': {
      for (const d of sim.state.recentDeaths) {
        if (d.escortOf !== s.id) continue;
        if (sim.state.time - d.at <= 1) return s.id;
      }
      return null;
    }
    case 'enemyInRange': {
      // nearest enemy squad inside [minRange, range] — deterministic id tie-break
      let best: Squad | null = null;
      let bestD = Infinity;
      for (const e of sim.state.squads.values()) {
        if (!sim.areHostile(e.owner, s.owner) || e.mountedOnWall !== null || e.mountedOnSquad !== null) continue;
        const d = dist(e.pos, s.pos);
        if (d > t.range || (t.minRange !== undefined && d < t.minRange)) continue;
        if (d < bestD - 1e-9 || (best !== null && Math.abs(d - bestD) <= 1e-9 && e.id < best.id)) {
          bestD = d;
          best = e;
        }
      }
      return best?.id ?? null;
    }
  }
}

function applyEffects(sim: Sim, source: Squad, def: AbilityDef, targetId: EntId): void {
  // resolve target entity
  const targetSquad = sim.state.squads.get(targetId);
  for (const eff of def.effects) {
    applyEffectToSquad(sim, source, eff, targetSquad ?? null);
  }
  // nth-attack counters reset on fire
  if (def.trigger.when === 'nthAttackVsStructure') source.structureAttackCount = 0;
  sim.pushEvent({
    type: 'AbilityTriggered',
    id: source.id,
    abilityId: def.id,
    pos: { ...source.pos },
    label: def.ui.label,
  });
}

function applyEffectToSquad(sim: Sim, source: Squad, eff: EffectRef, target: Squad | null): void {
  switch (eff.fx) {
    case 'damage':
      if (target) sim.damageSquad(target, eff.amount, { isRanged: false, attackerOwner: source.owner });
      break;
    case 'heal':
      if (target) {
        target.health = Math.min(target.maxHealth, target.health + eff.amount * target.healingTakenMult);
      }
      break;
    case 'tempHealth':
      if (target) {
        target.tempHealth = Math.max(target.tempHealth, eff.amount);
        target.tempHealthRemaining = eff.duration;
      }
      break;
    case 'statMod':
      if (target) {
        target.buffs.push({ stat: eff.stat as never, mult: eff.mult, remaining: eff.duration || Infinity });
      }
      break;
    case 'slow':
      if (target) target.buffs.push({ stat: 'moveSpeed', mult: 1 - eff.magnitude, remaining: eff.duration });
      break;
    case 'mark':
      if (target) target.marks.push({ markId: eff.markId, remaining: eff.duration });
      break;
    case 'teleport': {
      // self-teleport relative to the current target (or facing when targetless)
      const anchor =
        source.targetId !== null
          ? (sim.state.squads.get(source.targetId)?.pos ?? sim.state.buildings.get(source.targetId)?.pos ?? null)
          : null;
      let dir: { x: number; y: number };
      if (anchor) {
        const d = Math.max(0.001, dist(source.pos, anchor));
        const toTarget = { x: (anchor.x - source.pos.x) / d, y: (anchor.y - source.pos.y) / d };
        dir = eff.direction === 'toward-target' ? toTarget : { x: -toTarget.x, y: -toTarget.y };
      } else {
        const back = source.facing + Math.PI;
        dir = { x: Math.cos(back), y: Math.sin(back) };
      }
      const dest = vec(source.pos.x + dir.x * eff.distance, source.pos.y + dir.y * eff.distance);
      sim.teleportSquad(source, dest, eff.distance);
      break;
    }
    case 'knockback': {
      if (!target) break;
      const d = Math.max(0.001, dist(source.pos, target.pos));
      const dest = vec(
        target.pos.x + ((target.pos.x - source.pos.x) / d) * eff.distance,
        target.pos.y + ((target.pos.y - source.pos.y) / d) * eff.distance,
      );
      sim.teleportSquad(target, dest, eff.distance);
      break;
    }
    case 'spawnTemporary': {
      for (let i = 0; i < eff.count; i++) {
        const angle = (i / eff.count) * Math.PI * 2;
        const pos = vec(source.pos.x + Math.cos(angle) * 2, source.pos.y + Math.sin(angle) * 2);
        sim.spawnSquad(eff.unitId, source.owner, pos, { temporarySeconds: eff.duration });
      }
      break;
    }
    case 'areaDamage': {
      const center = target?.pos ?? source.pos;
      for (const e of [...sim.state.squads.values()]) {
        if (!sim.areHostile(e.owner, source.owner)) continue;
        if (dist(e.pos, center) <= eff.radius) {
          sim.damageSquad(e, eff.amount, { isRanged: false, attackerOwner: source.owner });
        }
      }
      break;
    }
    case 'healLowestAlly': {
      let lowest: Squad | null = null;
      for (const e of sim.state.squads.values()) {
        if (!sim.areAllies(e.owner, source.owner) || e.id === source.id) continue;
        if (e.health >= e.maxHealth) continue;
        if (dist(e.pos, source.pos) > eff.range) continue;
        if (!lowest || e.health / e.maxHealth < lowest.health / lowest.maxHealth) lowest = e;
      }
      if (lowest) {
        lowest.health = Math.min(lowest.maxHealth, lowest.health + eff.amount * lowest.healingTakenMult);
      }
      break;
    }
    case 'root':
      if (target) target.rootedRemaining = Math.max(target.rootedRemaining, eff.duration);
      break;
    case 'auraBuff': {
      for (const e of sim.state.squads.values()) {
        if (!sim.areAllies(e.owner, source.owner)) continue;
        if (dist(e.pos, source.pos) > eff.radius) continue;
        e.buffs.push({ stat: eff.stat as never, mult: eff.mult, remaining: eff.duration });
      }
      break;
    }
    case 'structureStrike': {
      const b = source.targetId !== null ? sim.state.buildings.get(source.targetId) : undefined;
      if (b) {
        sim.damageBuilding(b, eff.damage, { isSiege: true, attackerOwner: source.owner });
        b.repairHalvedRemaining = Math.max(b.repairHalvedRemaining, eff.repairHalfSeconds);
      }
      break;
    }
    case 'plantSapling': {
      // behind the source, on walkable ground clear of structures
      const back = source.facing + Math.PI;
      const pos = sim.nav.nearestWalkable(
        vec(source.pos.x + Math.cos(back) * 4, source.pos.y + Math.sin(back) * 4),
      );
      sim.spawnBuilding(eff.buildingId, source.owner, pos, { complete: true, expiresSeconds: eff.duration });
      break;
    }
    case 'healReduction':
      if (target) {
        target.healingTakenMult = Math.min(target.healingTakenMult, eff.mult);
        target.healingTakenRemaining = Math.max(target.healingTakenRemaining, eff.duration);
      }
      break;
    case 'consumeCorpseMarks': {
      const got = consumeMarksNear(sim, source.pos, eff.count, eff.radius);
      if (got > 0) {
        if (eff.healPer > 0) {
          source.health = Math.min(
            source.maxHealth,
            source.health + got * eff.healPer * source.healingTakenMult,
          );
        }
        if (eff.essencePer > 0) sim.state.players[source.owner].essence += got * eff.essencePer;
      }
      break;
    }
    case 'charge': {
      // dash at the triggering enemy — movement.ts drives the forced move each tick
      if (!target || !sim.areHostile(target.owner, source.owner)) break;
      // Proximity alone is insufficient: enemies can be close across a castle wall. Do not
      // even arm a dash unless the target shares a reachable, straight planning corridor.
      if (
        !physicalCorridorClear(sim, source.pos, target.pos, source.owner, squadBodyRadius(source))
      ) break;
      source.charge = {
        targetId: target.id,
        remaining: eff.durationSeconds,
        speedMult: eff.speedMult,
        impactDamage: eff.impactDamage ?? 0,
        impactSlowMult: eff.impactSlowMult ?? 1,
        impactSlowSeconds: eff.impactSlowSeconds ?? 0,
        impactKnockback: eff.impactKnockback ?? 0,
        impactKnockdownSeconds: eff.impactKnockdownSeconds ?? 0,
      };
      break;
    }
    case 'crushGrab': {
      // nearest small enemy squad in reach gets hoisted; N members die on the spot
      let victim: Squad | null = null;
      let bestD = Infinity;
      for (const e of sim.state.squads.values()) {
        if (!sim.areHostile(e.owner, source.owner) || e.mountedOnWall !== null || e.mountedOnSquad !== null) continue;
        if (unitDef(e.defId).sizeTag !== eff.maxTargetSize) continue;
        const d = dist(e.pos, source.pos);
        if (d > eff.range) continue;
        if (d < bestD - 1e-9 || (victim !== null && Math.abs(d - bestD) <= 1e-9 && e.id < victim.id)) {
          bestD = d;
          victim = e;
        }
      }
      if (!victim) break;
      const vd = unitDef(victim.defId);
      const perMember = victim.maxHealth / vd.squadMemberCount;
      // pre-compensate armor + temp health so exactly `kills` members drop
      const need = perMember * eff.kills + victim.tempHealth;
      const raw = need / Math.max(0.05, 1 - armorReductionOf(vd.armor));
      sim.pushEvent({
        type: 'GiantCrush',
        giantId: source.id,
        victimDefId: victim.defId,
        pos: { ...victim.pos },
      });
      sim.damageSquad(victim, raw, { isRanged: false, attackerOwner: source.owner });
      break;
    }
  }
}

const ESCORT_SOURCE = 'escort-class';

/** Procession of Arms (Bible §14.5): reapply one short buff per distinct escort class. */
function applyEscortClassBonuses(sim: Sim, boss: Squad): void {
  const extras = defs.bossExtras[boss.defId];
  const cfg = extras?.escortClassBonuses;
  if (!cfg) return;
  // strip last tick's bonuses first — reapplication must never stack
  for (let i = boss.buffs.length - 1; i >= 0; i--) {
    if ((boss.buffs[i] as { source?: string }).source === ESCORT_SOURCE) boss.buffs.splice(i, 1);
  }
  let hasInfantry = false;
  let hasRanged = false;
  let hasSiege = false;
  let hasSupport = false;
  for (const e of sim.state.squads.values()) {
    if (e.escortOf !== boss.id) continue;
    const ed = unitDef(e.defId);
    if (ed.roleTags.includes('frontline')) hasInfantry = true;
    if (ed.roleTags.includes('ranged')) hasRanged = true;
    if (ed.roleTags.includes('siege') || ed.sizeTag === 'large' || ed.sizeTag === 'giant') hasSiege = true;
    if (ed.roleTags.includes('support')) hasSupport = true;
  }
  const push = (stat: Squad['buffs'][number]['stat'], mult: number) =>
    boss.buffs.push(Object.assign({ stat, mult, remaining: 0.11 }, { source: ESCORT_SOURCE }));
  if (hasInfantry && cfg.infantryDamageTakenMult) push('damageTaken', cfg.infantryDamageTakenMult);
  if (hasRanged && cfg.rangedAttackSpeedMult) push('attackSpeed', cfg.rangedAttackSpeedMult);
  if (hasSiege && cfg.siegeStructureDamageMult) push('structureDamage', cfg.siegeStructureDamageMult);
  if (hasSupport && cfg.supportRegenPerSecond && sim.state.time - boss.lastDamagedAt > 3) {
    boss.health = Math.min(boss.maxHealth, boss.health + cfg.supportRegenPerSecond * dt * boss.healingTakenMult);
  }
}

export function stepAbilities(sim: Sim): void {
  const st = sim.state;
  for (const s of [...st.squads.values()]) {
    // escort-class boss bonuses reapply each tick (previous 0.11 s buffs decay below)
    if (s.isBoss) applyEscortClassBonuses(sim, s);
    // ---- housekeeping runs for EVERY squad (buffs from mines/spells must decay even
    // on ability-less squads — the old early-out left e.g. mine slows permanent)
    for (let i = s.buffs.length - 1; i >= 0; i--) {
      const b = s.buffs[i];
      if (b.remaining !== Infinity) {
        b.remaining -= dt;
        if (b.remaining <= 0) s.buffs.splice(i, 1);
      }
    }
    for (let i = s.marks.length - 1; i >= 0; i--) {
      s.marks[i].remaining -= dt;
      if (s.marks[i].remaining <= 0) s.marks.splice(i, 1);
    }
    if (s.tempHealth > 0) {
      s.tempHealthRemaining -= dt;
      if (s.tempHealthRemaining <= 0) s.tempHealth = 0;
    }
    if (s.rootedRemaining > 0) s.rootedRemaining -= dt;
    if (s.knockdownRemaining > 0) {
      s.knockdownRemaining = Math.max(0, s.knockdownRemaining - dt);
      if (s.knockdownRemaining <= 0) s.knockdownTotal = 0;
    }
    if (s.healingTakenRemaining > 0) {
      s.healingTakenRemaining -= dt;
      if (s.healingTakenRemaining <= 0) s.healingTakenMult = 1;
    }
    // temporary units expire WITHOUT death effects (no corpse marks, no death triggers)
    if (s.temporaryRemaining !== null) {
      s.temporaryRemaining -= dt;
      if (s.temporaryRemaining <= 0) {
        sim.despawnSquad(s);
        continue;
      }
    }

    const d = unitDef(s.defId);
    if (d.abilities.length === 0) continue;
    if (s.knockdownRemaining > 0) continue;

    // cooldowns
    for (const key of Object.keys(s.abilityCooldowns)) {
      if (s.abilityCooldowns[key] > 0) s.abilityCooldowns[key] -= dt;
    }

    // resolve active telegraph
    if (s.telegraph) {
      const telegraphDef = abilityDef(s.telegraph.abilityId);
      // Direct movement orders must remain authoritative. Without this guard a charge
      // queued on the previous tick could finish after the click and seize movement
      // control again, making large units appear to ignore move/withdraw commands.
      if (hasChargeEffect(telegraphDef) && !chargeAllowedByCommand(s)) {
        s.telegraph = null;
        continue;
      }
      s.telegraph.remaining -= dt;
      if (s.telegraph.remaining <= 0) {
        const def = telegraphDef;
        const targetId =
          def.targetSelector === 'self'
            ? s.id
            : (s.telegraph.targetId ?? (def.targetSelector === 'currentTarget' ? (s.targetId ?? s.id) : s.id));
        // re-validate: skip (fizzle) if target vanished
        if (def.targetSelector === 'self' || sim.state.squads.has(targetId) || sim.state.buildings.has(targetId)) {
          applyEffects(sim, s, def, targetId);
          if (def.trigger.when === 'selfBelowHealth' && def.trigger.oncePerLife) {
            s.abilityFiredOnce[def.id] = true;
          }
          s.abilityCooldowns[def.id] = def.internalCooldown;
        } else {
          s.abilityCooldowns[def.id] = def.internalCooldown * 0.5; // fizzle: 50% CD (§18.1)
        }
        s.telegraph = null;
      }
      continue; // max one telegraph per squad (§18.5)
    }

    // trigger scan
    for (const abId of d.abilities) {
      if ((s.abilityCooldowns[abId] ?? 0) > 0) continue;
      const def = abilityDef(abId);
      if (hasChargeEffect(def) && !chargeAllowedByCommand(s)) continue;
      const trigId = evalTrigger(sim, s, def);
      if (trigId !== null) {
        if (hasChargeEffect(def)) {
          const victim = sim.state.squads.get(trigId);
          if (
            !victim ||
            !sim.nav.canReach(s.pos, victim.pos, s.owner) ||
            !sim.nav.lineOfSight(s.pos, victim.pos, s.owner)
          ) continue;
        }
        s.telegraph = { abilityId: abId, remaining: def.telegraphSeconds, targetId: trigId };
        break;
      }
    }
  }
}

/** Shared buff/mark helpers for entities other systems create (mines, spells). */
export { applyEffectToSquad };
